import { Button, Grid, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  IExtendedUser,
  IFreeTextContent,
  ILabelGroupContent,
  IUser,
  QuestionContent,
  QuestionResponse,
  QuestionType
} from "src/common/types";
import ResponseContent from "./ResponseContent";

const useStyles = makeStyles((theme: any) => ({
  container: {
    margin: "0 0 16px"
  },
  mobileResponsesWrapper: {
    "@media print": {
      display: "none" as "none"
    },
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  mobileTabContainer: {
    "@media print": {
      display: "none" as "none"
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex",
    justifyContent: "space-evenly",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  mobileTabLabel: {
    fontSize: 12,
    height: "100%",
    textTransform: "none" as "none",
    width: "100%"
  },
  noReponse: {
    "@media print": {
      color: "#e4e3e3"
    },
    color: "#909090"
  },
  questionContainer: {
    "& > span": {
      display: "inline-block",
      lineHeight: "2.1rem",
      verticalAlign: "middle" as "middle"
    },
    "@media print": {
      borderBottom: "1px solid #e6e6e6 !important"
    },
    [theme.breakpoints.down("sm")]: {
      borderBottom: "none"
    },
    borderBottom: "1px solid #e6e6e6",
    lineHeight: "5.7rem",
    padding: "6px 24px"
  },
  response: {
    "& .public-DraftStyleDefault-block": {
      margin: 0
    }
  },
  responseContainer: {
    "&:first-child": {
      borderRight: "1px solid #e6e6e6"
    }
  },
  responsesWrapper: {
    "@media print": {
      display: "block" as "block"
    },
    display: "none" as "none",
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  tabBtnContainer: {
    textAlign: "center" as "center",
    width: "50%"
  },
  tabBtnHighlighted: {
    borderBottom: "1px solid #4a90e2",
    borderRadius: 0,
    color: "#4a90e2"
  },
  textContent: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 3,
    color: "#777777",
    fontSize: "1.4rem"
  }
}));

interface IOwnProps {
  leftPlaceholder: string;
  leftResponse: QuestionResponse | string | null;
  leftType: QuestionType | null;
  leftContent: QuestionContent | null;
  currentOrOtherParticipant?: IUser;
  rightPlaceholder: string;
  rightResponse: QuestionResponse | string | null;
  rightType: QuestionType | null;
  rightContent: QuestionContent | null;
}

const Response = ({
  leftType,
  leftPlaceholder,
  leftResponse,
  leftContent,
  currentOrOtherParticipant,
  rightType,
  rightPlaceholder,
  rightResponse,
  rightContent
}: IOwnProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState("myAnswer");

  const handleTabChange = (tab: string) => () => setActiveTab(tab);

  const questionLabel = React.useMemo(() => {
    if (leftType === QuestionType.LabelGroup) {
      const thisContent = leftContent as ILabelGroupContent;
      return thisContent.text;
    } else {
      return null;
    }
  }, [leftType, leftContent]);

  const questionHeading = React.useMemo(() => {
    if (leftContent) {
      if (leftType !== QuestionType.LabelGroup) {
        return (
          <Typography variant="h3">
            {t((leftContent as IFreeTextContent).text, {
              name: (currentOrOtherParticipant as IExtendedUser).firstName
            })}
          </Typography>
        );
      } else {
        return null;
      }
    } else if (rightContent) {
      if (rightType !== QuestionType.LabelGroup) {
        return (
          <Typography variant="h3">
            {t((rightContent as IFreeTextContent).text, {
              name: (currentOrOtherParticipant as IExtendedUser).firstName
            })}
          </Typography>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [leftContent, leftType, rightContent, rightType]);

  return (
    <Grid className={[classes.container, "print-avoid-break"].join(" ")} item xs={12}>
      {questionHeading}
      <Grid className={classes.textContent} container>
        {questionLabel ? (
          <Grid className={classes.questionContainer} item xs={12}>
            <span>{t(questionLabel)}</span>
          </Grid>
        ) : null}
        <Grid container className={classes.mobileTabContainer}>
          <Grid item className={classes.tabBtnContainer}>
            <Button
              className={
                activeTab === "myAnswer"
                  ? [classes.mobileTabLabel, classes.tabBtnHighlighted].join(" ")
                  : classes.mobileTabLabel
              }
              onClick={handleTabChange("myAnswer")}
            >
              {t(`feedback.page.talk-sheet.myAnswer`)}
            </Button>
          </Grid>
          <Grid item className={classes.tabBtnContainer}>
            <Button
              className={
                activeTab === "dsvAnswer"
                  ? [classes.mobileTabLabel, classes.tabBtnHighlighted].join(" ")
                  : classes.mobileTabLabel
              }
              onClick={handleTabChange("dsvAnswer")}
            >
              {t(`feedback.page.talk-sheet.dsvAnswer`, {
                name: currentOrOtherParticipant ? currentOrOtherParticipant.firstName : ""
              })}
            </Button>
          </Grid>
        </Grid>
        <Grid className={classes.mobileResponsesWrapper} item xs={12}>
          <Grid container>
            <Grid className={classes.responseContainer} container item xs={12}>
              <ResponseContent
                type={activeTab === "myAnswer" ? leftType : rightType}
                content={activeTab === "myAnswer" ? leftContent : rightContent}
                placeholder={activeTab === "myAnswer" ? leftPlaceholder : rightPlaceholder}
                response={activeTab === "myAnswer" ? leftResponse || null : rightResponse || null}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classes.responsesWrapper} item xs={12}>
          <Grid container>
            <Grid className={classes.responseContainer} container item xs={6}>
              <ResponseContent
                type={leftType}
                content={leftContent}
                placeholder={leftPlaceholder}
                response={leftResponse || null}
              />
            </Grid>
            <Grid className={classes.responseContainer} container item xs={6}>
              <ResponseContent
                type={rightType}
                content={rightContent}
                placeholder={rightPlaceholder}
                response={rightResponse || null}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(Response);
